import { GLOBAL_POPPER_IGNORE_ATTR } from "~/constants/dropdown";

import { type GlobalPopperTrigger, PopperType } from "./shared";

const MAX_ACTIVE_POPPERS_PER_TYPE = 4;
const MAX_POPPER_DEPTH = 4;

let listenerAttached = false;
let nextPopperId = 0;

const activePoppers = new Map<PopperType, Map<number, () => boolean>>();
const activePopperTypes = new Map<number, PopperType>();
const parentChildMap = new Map<number, number>();
const childrenMap = new Map<number, Set<number>>();

const isAncestorOf = (ancestorId: number, descendantId: number) => {
  let current = descendantId;
  for (let i = 0; i < 100; i += 1) {
    const parent = parentChildMap.get(current);
    if (parent === undefined) {
      break;
    }
    if (parent === ancestorId) {
      return true;
    }
    current = parent;
  }
  return false;
};

const hasActiveAncestor = (popperId: number, activeIds: number[]) => {
  let current = popperId;
  for (let i = 0; i < 100; i += 1) {
    const parent = parentChildMap.get(current);
    if (parent === undefined) {
      break;
    }
    if (activeIds.includes(parent)) {
      return true;
    }
    current = parent;
  }
  return false;
};

export const getNextPopperId = () => {
  nextPopperId += 1;
  return nextPopperId;
};

export const registerChild = (childId: number, parentId: number) => {
  parentChildMap.set(childId, parentId);
  if (!childrenMap.has(parentId)) {
    childrenMap.set(parentId, new Set());
  }
  const children = childrenMap.get(parentId);
  if (children) {
    children.add(childId);
  }
};

export const unregisterChild = (childId: number) => {
  const parentId = parentChildMap.get(childId);
  if (parentId !== undefined) {
    childrenMap.get(parentId)?.delete(childId);
  }
  parentChildMap.delete(childId);
};

export const hasChildren = (popperId: number) =>
  Array.from(childrenMap.get(popperId) ?? []).some((childId) => activePopperTypes.get(childId) !== PopperType.TOOLTIP);

export const registerActivePopper = (type: PopperType, id: number, deactivate: () => boolean) => {
  let typePoppers = activePoppers.get(type);
  if (!typePoppers) {
    typePoppers = new Map();
    activePoppers.set(type, typePoppers);
  }
  const overflowCount = typePoppers.size - MAX_ACTIVE_POPPERS_PER_TYPE + 1;

  if (overflowCount > 0) {
    const entries = Array.from(typePoppers.entries());
    const allActiveIds = entries.map(([popperId]) => popperId);

    const canDeactivate = ([popperId]: [number, () => boolean]) =>
      !hasChildren(popperId) &&
      !allActiveIds.some((activeId) => activeId !== popperId && isAncestorOf(popperId, activeId)) &&
      !hasActiveAncestor(popperId, allActiveIds);

    entries
      .filter(canDeactivate)
      .slice(0, overflowCount)
      .forEach(([popperId, deactivateFn]) => {
        if (deactivateFn()) {
          typePoppers.delete(popperId);
        }
      });
  }

  typePoppers.set(id, deactivate);
  activePopperTypes.set(id, type);
  return id;
};

export const unregisterActivePopper = (type: PopperType, id: number) => {
  activePoppers.get(type)?.delete(id);
  activePopperTypes.delete(id);
};

const handlePopperEvent = (target: EventTarget | null, includeShow: boolean) => {
  if (!(target instanceof Element)) {
    return;
  }

  if (target.closest(`[${GLOBAL_POPPER_IGNORE_ATTR}]`)) {
    return;
  }

  const poppers: GlobalPopperTrigger[] = [];
  let current: Element | null = target;
  for (let i = 0; i < MAX_POPPER_DEPTH && current; i += 1) {
    const popper: Element | null = current.closest("[data-global-popper]");
    if (!popper) {
      break;
    }
    poppers.push(popper as GlobalPopperTrigger);
    current = popper.parentElement;
  }

  [...poppers].reverse().forEach((popperEl) => {
    popperEl.__activatePopper?.();
    if (includeShow) {
      popperEl.__showPopper?.();
    }
  });
};

export const initGlobalPopper = () => {
  if (listenerAttached) {
    return;
  }
  listenerAttached = true;

  document.addEventListener("mouseenter", (e) => handlePopperEvent(e.target, false), { capture: true, passive: true });
  document.addEventListener("click", (e) => handlePopperEvent(e.target, true), { capture: true, passive: true });
  document.addEventListener(
    "contextmenu",
    () => {
      const tooltips = activePoppers.get(PopperType.TOOLTIP);
      tooltips?.forEach((deactivate, id) => deactivate() && tooltips.delete(id));
    },
    { capture: true, passive: true }
  );
};
